import pt from 'prop-types';
import { Component } from 'react';
import { ConfigProps, InjectedFormProps, reduxForm } from 'redux-form';
import { DataTestId } from '@mews-commander/enums';
import {
    CommanderContext,
    CommanderFormContextValues,
    CommanderFormProvider,
    ConfirmationDialog,
    ConfirmationDialogProps,
    FieldsContainer,
    LegacyCommanderContextValue,
    OverlayedLoader,
} from '@mews-commander/ui';
import { KeyValue } from '@mews/enums';
import { DateTimeLocalizationInfo, lt, showErrorAlert } from '@mews/framework';
import { OptionalKeys, RequireFields } from '@mews/std';
import { Button, ButtonProps, Stack } from '@optimus-web/core';

interface OwnFormProps extends Partial<CommanderFormContextValues> {
    confirmationDialogTitle?: ConfirmationDialogProps['textKey'];
    confirmationDialogContent?: ConfirmationDialogProps['content'];
    confirmationDialogConfirmButtonTextKey?: ConfirmationDialogProps['confirmButtonTextKey'];
    confirmationDialogConfirmButtonVariant?: ConfirmationDialogProps['confirmButtonVariant'];
    showConfirmationDialog?: boolean;
    submitButtonProps?: Omit<ButtonProps, 'type'> & { label?: React.ReactNode };
}

type CombinedProps<P> = P & OwnFormProps;

interface CommanderFormFactoryParams<V, P>
    extends OptionalKeys<ConfigProps<V, CombinedProps<P>>, 'onSubmitFail' | 'form'>,
        Partial<OwnFormProps> {
    ButtonGroupElement?: React.ComponentType<RequireFields<React.PropsWithChildren<{}>, 'children'>>;
    fixedLoader?: boolean;
    FormElement?: React.ComponentType<
        RequireFields<React.PropsWithChildren<React.DOMAttributes<HTMLElement>>, 'children'>
    >;
    secondaryButton?: React.ReactNode;
    submitLabel?: React.ReactNode;
}

type WrappedComponentProps<V, P> = P &
    Pick<OwnFormProps, 'showConfirmationDialog'> &
    Partial<Omit<InjectedFormProps<V, CombinedProps<P>>, 'handleSubmit'>>;

type FormProps<V, P> = CombinedProps<P> & InjectedFormProps<V, CombinedProps<P>>;

/**
 * @deprecated Please use `Form` component or `useFormControls` hook from `@mews-commander/form`.
 */
export const commanderForm =
    <V extends object, P>({
        submitLabel,
        secondaryButton = null,
        submitButtonProps: defaultSubmitButtonProps,
        FormElement = FieldsContainer,
        ButtonGroupElement = (props) => <Stack {...props} spacing="100" itemAlignment="center" alignment="end" />,

        onSubmitFail = (error, dispatch, submissionError) => {
            if (error) {
                showErrorAlert({ description: lt('ValidationErrorMessage') });
            }

            if (submissionError) {
                throw submissionError;
            }
        },
        fixedLoader = false,
        validate,
        ...reduxFormOptions
    }: CommanderFormFactoryParams<V, P>) =>
    (WrappedComponent: React.ComponentType<WrappedComponentProps<V, P>>) => {
        let dateTimeLocalizationInfo: DateTimeLocalizationInfo | null;
        class Form extends Component<FormProps<V, P>> {
            declare context: LegacyCommanderContextValue;
            static contextType = CommanderContext;

            static propTypes = {
                handleSubmit: pt.func.isRequired,
                submitting: pt.bool.isRequired,
                inlined: pt.bool,
                readOnlyForm: pt.bool,
            };

            state = {
                dialogOpened: false,
            };

            onSubmitDialog: React.MouseEventHandler = (e) => {
                this.closeDialog();
                this.props.handleSubmit(e);
            };

            handleKeyDown: React.KeyboardEventHandler = (e) => {
                const formDisabled = this.props.submitButtonProps?.disabled;

                if (e.key === KeyValue.Enter && e.shiftKey === false && !formDisabled) {
                    this.handleSubmit(e);
                }
            };

            handleSubmit: React.EventHandler<React.SyntheticEvent> = (e) => {
                const { confirmationDialogTitle, showConfirmationDialog, handleSubmit, valid } = this.props;

                if (valid && !!showConfirmationDialog && confirmationDialogTitle) {
                    this.openDialog();
                } else {
                    handleSubmit(e);
                }
            };

            openDialog = () => this.setState({ dialogOpened: true });

            closeDialog = () => this.setState({ dialogOpened: false });

            render() {
                dateTimeLocalizationInfo = this.context && this.context.getDateTimeLocalizationInfo();

                const {
                    confirmationDialogTitle,
                    confirmationDialogContent,
                    confirmationDialogConfirmButtonTextKey,
                    confirmationDialogConfirmButtonVariant,
                    submitButtonProps = {},

                    // omitted props
                    handleSubmit,
                    readOnlyForm,

                    ...wrappedComponentProps
                } = this.props;
                const { form, submitting } = this.props;
                const { label, disabled, ...otherSubmitButtonProps } = {
                    ...defaultSubmitButtonProps,
                    ...submitButtonProps,
                };

                return (
                    <div>
                        <FormElement onKeyDown={this.handleKeyDown} data-test-form={form}>
                            <CommanderFormProvider readOnlyForm={readOnlyForm}>
                                <WrappedComponent {...(wrappedComponentProps as WrappedComponentProps<V, P>)} />
                            </CommanderFormProvider>
                            {!readOnlyForm && (
                                <ButtonGroupElement>
                                    {secondaryButton}
                                    <Button
                                        onClick={this.handleSubmit}
                                        disabled={disabled || submitting}
                                        data-test-id={DataTestId.FormMainButton}
                                        {...otherSubmitButtonProps}
                                        type="submit"
                                    >
                                        {label || submitLabel}
                                    </Button>
                                </ButtonGroupElement>
                            )}
                        </FormElement>
                        <OverlayedLoader show={submitting} fixed={fixedLoader} />
                        {confirmationDialogTitle && (
                            <ConfirmationDialog
                                confirmationOpened={this.state.dialogOpened}
                                cancelChange={this.closeDialog}
                                confirmChange={this.onSubmitDialog}
                                textKey={confirmationDialogTitle}
                                content={confirmationDialogContent}
                                confirmButtonTextKey={confirmationDialogConfirmButtonTextKey}
                                confirmButtonVariant={confirmationDialogConfirmButtonVariant}
                            />
                        )}
                    </div>
                );
            }
        }

        return reduxForm<V, CombinedProps<P> & { children?: React.ReactNode }>({
            onSubmitFail,
            validate: validate
                ? (validateValues, validateProps) =>
                      validate(validateValues, { ...validateProps, dateTimeLocalizationInfo })
                : undefined,
            ...reduxFormOptions,
        })(Form);
    };
